/**
 * @fileoverview Builds read-only simulated chat content from connection requests.
 * @module pages/chat/chatSimulatedMessage
 */
import {
  formatLongDateLocalized,
  formatTimeWithSuffix,
  durationToMinutes,
} from "./chatFormatters.js";

/**
 * Builds the full simulated chat message body for a connection request.
 * @param {Object} request - Connection request payload
 * @param {Array} users - All users (for partner name lookup)
 * @param {Function} t - i18n translate function
 * @returns {{ fullMessage: string, buyerName: string, selectedUser: Object|null }}
 */
export const buildSimulatedChatContent = (request, users, t) => {
  const currentUserId = localStorage.getItem("userId");
  const partnerName =
    users.find((u) => u._id === currentUserId)?.nombre || "";
  const buyerName = request?.senderUser?.nombre || request?.nombre || "";
  const buyerCompanyName = request?.senderUserCompany?.nombreComercial || "";
  const eventName = request?.businessEvent?.nombre || "";
  const proposedDate = formatLongDateLocalized(
    request?.appointment?.diaCitaVirtual,
    t?.i18n?.language,
  );
  const proposedTime = formatTimeWithSuffix(
    request?.appointment?.horaInicioCitaVirtual,
  );
  const durationMinutes = durationToMinutes(
    request?.appointment?.duracionCitaVirtual,
  );

  let fullMessage;

  if (request?.type === "customerCompanyUserConnectionRequest") {
    const titleLine = t(
      "pages.chat.customerCompanyUserConnectionRequestMessage.titleLine",
      { partnerName },
    );
    const body1 = t(
      "pages.chat.customerCompanyUserConnectionRequestMessage.body1",
      { buyerName, eventName, date: proposedDate, time: proposedTime },
    );
    const body2 = t(
      "pages.chat.customerCompanyUserConnectionRequestMessage.body2",
    );
    const body3 = t(
      "pages.chat.customerCompanyUserConnectionRequestMessage.body3",
    );
    fullMessage = `${titleLine}\n\n${body1}\n\n${body2}\n\n${body3}`;
  } else {
    const titleLine = t("pages.chat.autoVirtualMessage.titleLine", {
      partnerName,
    });
    const body1 = t("pages.chat.autoVirtualMessage.body1", {
      buyerName,
      buyerCompanyName,
      eventName,
    });
    const proposal = t("pages.chat.autoVirtualMessage.proposal", {
      date: proposedDate,
      time: proposedTime,
      duration: `${durationMinutes} min`,
    });
    const body2 = t("pages.chat.autoVirtualMessage.body2");
    const body3 = t("pages.chat.autoVirtualMessage.body3");
    const signature = t("pages.chat.autoVirtualMessage.signature");
    fullMessage = `${titleLine}\n\n${body1}\n\n${proposal}\n\n${body2}\n\n${body3}\n\n${signature}`;
  }

  const selectedUser =
    users.find(
      (u) => u._id === (request?.senderUser?._id || request?.senderUserId),
    ) ||
    request?.senderUser ||
    null;

  return { fullMessage, buyerName, selectedUser };
};
